import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { MapPin, Clock, Banknote, Briefcase, ArrowLeft, Share2, CheckCircle2, Building2 } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl, formatApiError } from "@/lib/api";
import SEO from "@/components/SEO";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import VerifiedBadge from "@/components/VerifiedBadge";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

export default function JobDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [applied, setApplied] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/jobs/${slug}`).then((r) => {
      setJob(r.data);
      if (user?.role === "jobseeker") {
        api.get(`/jobs/${r.data.id}/application-status`).then((s) => setApplied(s.data.applied)).catch(() => {});
      }
    }).catch(() => setNotFound(true));
  }, [slug, user]);

  const apply = async () => {
    if (!user) { navigate("/giris"); return; }
    if (user.role !== "jobseeker") { toast.error("Başvuru için iş arayan hesabı gereklidir"); return; }
    setApplying(true);
    try {
      await api.post(`/jobs/${job.id}/apply`, { message: "" });
      setApplied(true);
      toast.success("Başvurunuz iletildi!", { description: "İletişim numaranız ve CV'niz işverene otomatik gönderildi." });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setApplying(false);
    }
  };

  const share = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("İlan bağlantısı kopyalandı");
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-32 text-center">
          <h1 className="text-2xl font-bold text-[#1f222d]">İlan bulunamadı</h1>
          <Link to="/ilanlar" className="text-blue-600 font-semibold mt-4 inline-block">Tüm ilanlara dön</Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="min-h-[50vh] flex items-center justify-center">
          <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  const jsonLd = {
    "@context": "https://schema.org/",
    "@type": "JobPosting",
    title: job.title,
    description: job.description,
    datePosted: job.created_at,
    employmentType: job.work_type,
    hiringOrganization: { "@type": "Organization", name: job.company_name },
    jobLocation: { "@type": "Place", address: { "@type": "PostalAddress", addressLocality: job.location, addressCountry: "TR" } },
    ...(job.salary ? { baseSalary: { "@type": "MonetaryAmount", currency: "TRY", value: { "@type": "QuantitativeValue", value: job.salary } } } : {}),
  };

  const logo = job.employer_logo_file_id ? fileUrl(job.employer_logo_file_id) : null;

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={`${job.title} - ${job.company_name} | isbul.org`}
        description={(job.description || "").slice(0, 160)}
        keywords={`${job.title}, ${job.company_name}, ${job.category_name}, iş ilanı, ${(job.skills || []).join(", ")}`}
        canonical={`/ilan/${job.slug}`}
        jsonLd={jsonLd}
      />
      <Header />

      <div className="max-w-5xl mx-auto px-4 lg:px-8 py-8">
        <button data-testid="back-btn" onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-slate-500 hover:text-[#1f222d] text-sm mb-6">
          <ArrowLeft size={16} /> Geri
        </button>

        <div className="grid lg:grid-cols-[1fr_320px] gap-8">
          <div>
            {/* Header card */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6 lg:p-8">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center overflow-hidden shrink-0">
                  {logo ? <img src={logo} alt={job.company_name} className="w-full h-full object-contain" /> : <Briefcase className="text-blue-600" size={28} />}
                </div>
                <div className="flex-1 min-w-0">
                  <h1 className="text-2xl sm:text-3xl font-bold text-[#1f222d] leading-tight">{job.title}</h1>
                  <div className="flex items-center gap-1.5 mt-2 text-slate-600">
                    <span className="font-medium">{job.company_name}</span>
                    {job.employer_verified && <VerifiedBadge size={17} />}
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mt-6">
                <span className="inline-flex items-center gap-1.5 bg-slate-50 border border-slate-100 rounded-lg px-3 py-1.5 text-sm text-slate-700"><MapPin size={15} /> {job.location}</span>
                <span className="inline-flex items-center gap-1.5 bg-slate-50 border border-slate-100 rounded-lg px-3 py-1.5 text-sm text-slate-700"><Clock size={15} /> {job.work_type}</span>
                <span className="inline-flex items-center gap-1.5 bg-slate-50 border border-slate-100 rounded-lg px-3 py-1.5 text-sm text-slate-700"><Briefcase size={15} /> {job.category_name}</span>
                {job.salary && <span className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 border border-blue-100 rounded-lg px-3 py-1.5 text-sm font-medium"><Banknote size={15} /> {job.salary}</span>}
              </div>
            </div>

            {/* Description */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6 lg:p-8 mt-5">
              <h2 className="text-xl font-bold text-[#1f222d] mb-4">İş Tanımı</h2>
              <p className="text-slate-700 leading-relaxed whitespace-pre-line">{job.description}</p>

              {job.requirements && (
                <>
                  <h2 className="text-xl font-bold text-[#1f222d] mb-4 mt-8">Aranan Nitelikler</h2>
                  <p className="text-slate-700 leading-relaxed whitespace-pre-line">{job.requirements}</p>
                </>
              )}

              {job.skills?.length > 0 && (
                <>
                  <h2 className="text-xl font-bold text-[#1f222d] mb-4 mt-8">Yetenekler</h2>
                  <div className="flex flex-wrap gap-2">
                    {job.skills.map((s) => (
                      <span key={s} className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 rounded-full px-3 py-1 text-sm font-medium">
                        <CheckCircle2 size={14} /> {s}
                      </span>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <aside className="space-y-5">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 lg:sticky lg:top-24">
              <Button data-testid="apply-btn" onClick={apply} disabled={applied || applying} className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-xl py-6 text-base font-semibold disabled:opacity-60">
                {applied ? "✓ Başvuruldu" : applying ? "Gönderiliyor..." : "Hemen Başvur"}
              </Button>
              {user?.role === "jobseeker" && !applied && (
                <p className="text-xs text-slate-400 mt-2 text-center">Başvuruda iletişim numaranız ve CV'niz otomatik iletilir.</p>
              )}
              <button data-testid="share-btn" onClick={share} className="w-full mt-3 inline-flex items-center justify-center gap-2 border border-slate-200 rounded-xl py-3 text-sm font-medium text-[#1f222d] hover:bg-slate-50 transition-colors">
                <Share2 size={16} /> Paylaş
              </button>
              <div className="mt-5 pt-5 border-t border-slate-100 text-sm text-slate-500 space-y-2">
                <div className="flex justify-between"><span>Görüntülenme</span><span className="font-medium text-[#1f222d]">{job.views || 0}</span></div>
                <div className="flex justify-between"><span>Deneyim</span><span className="font-medium text-[#1f222d]">{job.experience_level}</span></div>
              </div>
            </div>

            {job.company && (
              <div className="bg-white border border-slate-200 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Building2 size={18} className="text-blue-600" />
                  <h3 className="font-semibold text-[#1f222d]">Şirket Hakkında</h3>
                </div>
                <div className="flex items-center gap-1.5 font-medium text-[#1f222d]">
                  {job.company.company_name} {job.company.verified && <VerifiedBadge size={15} />}
                </div>
                {job.company.about && <p className="text-sm text-slate-500 mt-2 leading-relaxed">{job.company.about}</p>}
                {job.company.website && <a href={job.company.website} target="_blank" rel="noreferrer" className="text-sm text-blue-600 hover:underline mt-2 inline-block">{job.company.website}</a>}
              </div>
            )}
          </aside>
        </div>
      </div>

      <Footer />
    </div>
  );
}
